// Sugalarm — TTS owner.
//
// `/api/tts/speak` is the server path (whose own engine tiering is an
// internal detail of `server/services/tts_service.py`). The Web Speech API
// stays as the LAST-RESORT client fallback when the server path is
// unreachable, returns non-OK, or audio playback rejects.
//
// Cycle 330 — `engine` is NO LONGER part of the public surface. The
// household is never told which engine spoke, so the hook deliberately
// does not hand any component a value that could be rendered. The internal
// `engineRef` below still tracks which tier served the last utterance for
// local debugging, but it is not state, not returned, and must never be
// exposed to a component or surfaced in the UI. Do not re-add it.
//
// Public surface: speak / speakReminder / stop / speaking / text /
// progress / setVoice.
//
// `setVoice` is an imperative the App-level wiring calls each time the
// settings context's `tts_voice` changes — that way the hook always
// has the latest voice without being part of every parent re-render's
// dependency chain.

import { useCallback, useEffect, useRef, useState } from "react";

import { fetchSpeechBlob, type SpeechBlobHandle } from "../api/tts";

export interface SpeakOpts {
  rate?: number;
  pitch?: number;
  volume?: number;
  /** Override the voice for this single call. Defaults to the latest
   *  voice pushed in via `setVoice()`. */
  voice?: string;
}

/** Which tier served the last utterance. INTERNAL — debugging only; never
 *  returned from the hook and never rendered. "server" deliberately does
 *  not distinguish the server's own primary/fallback tiers: the client
 *  cannot see which one spoke, and must not be able to. */
type TTSEngine = "server" | "web-speech";

export interface TTSState {
  speak: (text: string, opts?: SpeakOpts) => void;
  /** Cycle 236: speak a fired reminder. Routes through the same `speak()`
   *  path (server voice → local fallback + the cycle-216 autoplay-unlock)
   *  after a short delay so the takeover overlay's DOM settles before audio
   *  starts. Relies on the already-primed element — does NOT unlock itself. */
  speakReminder: (text: string) => void;
  stop: () => void;
  speaking: boolean;
  text: string;
  progress: number;
  /** Push the latest preferred voice (from settings) into the hook. */
  setVoice: (voice: string | null) => void;
}

function getAudioElement(): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  return new Audio();
}

// Cycle 216: a tiny valid silent WAV (44-byte header, zero samples) used
// to prime the TTS <audio> element during a user gesture. Playing any
// clip inside a gesture marks the element as user-activated, so a later
// play() off a WebSocket frame (a spoken reminder, no gesture in scope)
// is no longer rejected by the autoplay policy.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";

export function useTTS(): TTSState {
  const [speaking, setSpeaking] = useState(false);
  const [text, setText] = useState("");
  const [progress, setProgress] = useState(0);
  // Cycle 330: a REF, not state — nothing re-renders on it and nothing can
  // read it out of the hook. Debug-only breadcrumb; never rendered.
  const engineRef = useRef<TTSEngine>("server");

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobRef = useRef<SpeechBlobHandle | null>(null);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);
  const voiceRef = useRef<string | null>(null);
  // Monotonic counter so a slow in-flight `speak` can't clobber a
  // newer utterance after it resolves.
  const reqIdRef = useRef(0);
  // Cycle 216: autoplay-unlock state for THIS hook's <audio> element.
  // `audioUnlockedRef` flips true after the first user gesture primes
  // the element; `pendingPlayRef` holds a speak() whose play() was
  // rejected pre-unlock so it can be replayed on the next gesture
  // (rather than dropped silently — the confirmed reminder regression).
  const audioUnlockedRef = useRef(false);
  const pendingPlayRef = useRef<{
    reqId: number;
    str: string;
    opts: SpeakOpts;
  } | null>(null);

  // Pre-create the audio element once.
  if (audioRef.current == null) {
    audioRef.current = getAudioElement();
  }

  const setVoice = useCallback((voice: string | null) => {
    voiceRef.current = voice && voice.trim() ? voice : null;
  }, []);

  const revokeBlob = useCallback(() => {
    if (blobRef.current) {
      blobRef.current.revoke();
      blobRef.current = null;
    }
  }, []);

  const speakViaWebSpeech = useCallback((str: string, opts: SpeakOpts) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSpeaking(false);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(str);
    // Cycle 330.3: match the calm, slower delivery the server path uses, so
    // dropping to this fallback doesn't suddenly sound urgent. Callers can
    // still override per-utterance.
    u.rate = opts.rate ?? 0.95;
    u.pitch = opts.pitch ?? 1.0;
    u.volume = opts.volume ?? 1.0;

    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) =>
      /samantha|google us english|en-us/i.test(`${v.name} ${v.lang}`),
    );
    if (preferred) u.voice = preferred;

    u.onstart = () => {
      setSpeaking(true);
      setText(str);
      setProgress(0);
      engineRef.current = "web-speech";
    };
    u.onend = () => {
      setSpeaking(false);
      setProgress(1);
    };
    u.onerror = () => setSpeaking(false);
    u.onboundary = (e: SpeechSynthesisEvent) => {
      if (e.charLength) {
        setProgress(Math.min(1, (e.charIndex + e.charLength) / str.length));
      }
    };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, []);

  const speak = useCallback(
    (str: string, opts: SpeakOpts = {}): void => {
      if (!str || !str.trim()) return;
      const reqId = ++reqIdRef.current;

      // Cancel any in-flight playback (Edge audio + Web Speech).
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      revokeBlob();
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }

      setText(str);
      setProgress(0);

      // Offline → fall straight through to the local fallback.
      // Cycle 330: client-visible console lines must not name an engine.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        // eslint-disable-next-line no-console
        console.debug("[tts] offline, using local fallback");
        speakViaWebSpeech(str, opts);
        return;
      }

      const voice = opts.voice ?? voiceRef.current ?? undefined;

      void (async () => {
        const r = await fetchSpeechBlob(str, voice);
        if (reqIdRef.current !== reqId) {
          // Superseded by a newer call — drop the blob and stop.
          if (r.ok) r.data.revoke();
          return;
        }
        if (!r.ok) {
          // eslint-disable-next-line no-console
          console.debug("[tts] server path failed, using local fallback");
          speakViaWebSpeech(str, opts);
          return;
        }
        const a = audioRef.current;
        if (!a) {
          r.data.revoke();
          speakViaWebSpeech(str, opts);
          return;
        }
        blobRef.current = r.data;
        a.src = r.data.url;
        a.volume = opts.volume ?? 1.0;
        try {
          a.currentTime = 0;
        } catch {
          /* ignore */
        }

        const onPlay = () => {
          if (reqIdRef.current !== reqId) return;
          setSpeaking(true);
          engineRef.current = "server";
        };
        const onEnded = () => {
          if (reqIdRef.current !== reqId) return;
          setSpeaking(false);
          setProgress(1);
          revokeBlob();
        };
        const onError = () => {
          if (reqIdRef.current !== reqId) return;
          // eslint-disable-next-line no-console
          console.debug("[tts] audio element error, falling back");
          revokeBlob();
          speakViaWebSpeech(str, opts);
        };
        const onTime = () => {
          if (reqIdRef.current !== reqId) return;
          if (a.duration && isFinite(a.duration)) {
            setProgress(Math.min(1, a.currentTime / a.duration));
          }
        };

        // Detach previous listeners on the same element so a leaked
        // listener from a superseded call can't flip state under us.
        a.onplay = onPlay;
        a.onended = onEnded;
        a.onerror = onError;
        a.ontimeupdate = onTime;

        try {
          await a.play();
        } catch (err) {
          if (reqIdRef.current !== reqId) return;
          // eslint-disable-next-line no-console
          console.debug("[tts] play() rejected", err);
          if (!audioUnlockedRef.current) {
            // Cycle 216: not yet unlocked (e.g. a wall kiosk no one
            // has touched since the page loaded). Queue this utterance
            // and replay it on the next user gesture rather than
            // dropping the spoken reminder silently. Keep the blob
            // alive for the replay — do NOT revoke here.
            pendingPlayRef.current = { reqId, str, opts };
            return;
          }
          // Already unlocked but still rejected — fall back to Web Speech.
          revokeBlob();
          speakViaWebSpeech(str, opts);
        }
      })();
    },
    [revokeBlob, speakViaWebSpeech],
  );

  // Cycle 236: speak a fired reminder. Defers the speak() by ~300ms so the
  // fullscreen <ReminderOverlay> mounts/settles before audio starts (reduces
  // the autoplay-unlock race on a wall kiosk). It calls through the existing
  // speak() path — the cycle-216 autoplay-unlock already primed the element,
  // so this never re-unlocks.
  const speakReminder = useCallback(
    (text: string) => {
      if (!text || !text.trim()) return;
      window.setTimeout(() => {
        speak(text);
      }, 300);
    },
    [speak],
  );

  const stop = useCallback(() => {
    reqIdRef.current++;
    const a = audioRef.current;
    if (a) {
      a.pause();
      try {
        a.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    revokeBlob();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, [revokeBlob]);

  // Cycle 216: prime this hook's <audio> element on the first user
  // gesture — the same autoplay-unlock pattern useAlarmSounds runs for
  // its alarm elements, applied to the element useTTS owns. Without it,
  // a spoken reminder fired off a WebSocket frame (no gesture in scope)
  // has its play() rejected by the browser/WebView autoplay policy and
  // is lost (the confirmed reminder-TTS regression). If a reminder was
  // dropped before the first gesture, replay it now (within the gesture
  // so play() is allowed), retrying once before falling back to Web
  // Speech.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let primed = false;
    const unlock = async () => {
      if (primed) return;
      primed = true;
      audioUnlockedRef.current = true;
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);

      const a = audioRef.current;
      const pending = pendingPlayRef.current;
      pendingPlayRef.current = null;
      if (
        a &&
        pending &&
        pending.reqId === reqIdRef.current &&
        a.getAttribute("src")
      ) {
        // A reminder was queued before this gesture — its blob src is
        // still on the element; play it now. The gesture activates the
        // element, so this succeeds; retry-failure falls to Web Speech.
        a.play().catch(() => {
          if (reqIdRef.current !== pending.reqId) return;
          revokeBlob();
          speakViaWebSpeech(pending.str, pending.opts);
        });
        return;
      }

      // Nothing queued — prime with a brief muted silent clip so the
      // element is unlocked for the next reminder.
      if (a) {
        const wasMuted = a.muted;
        a.muted = true;
        try {
          a.src = SILENT_WAV;
          const p = a.play();
          if (p && typeof p.then === "function") await p;
        } catch {
          /* may reject on some browsers even when muted */
        }
        try {
          a.pause();
          a.currentTime = 0;
        } catch {
          /* ignore */
        }
        a.removeAttribute("src");
        a.muted = wasMuted;
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [revokeBlob, speakViaWebSpeech]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      reqIdRef.current++;
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.removeAttribute("src");
      }
      if (blobRef.current) {
        blobRef.current.revoke();
        blobRef.current = null;
      }
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // NOTE: `engineRef` is intentionally NOT returned — see the header.
  return { speak, speakReminder, stop, speaking, text, progress, setVoice };
}
